import { useFormik } from "formik";
import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import Cookies from "js-cookie";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import {
  fetchOrganizations, // kept for the commented-out fetchOrganizationsData block below
  createUser,
  getUserInfoAPI,
  updateUser,
  getAllByOrganization,
  getAllByOrganizationId,
  getMultipleProject,
  getMultipleActivity,
  projectsForClockIn,
  getGroupsByDepartment,
  createGroup,
} from "../../service/User";
import {
  filterGroupsByUserAccess,
  currentUserCanUseGroups,
  currentUserCanCreateGroups,
} from "../../components/task/GroupAssignParts";
import { getRoleList } from "../../service/Role";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import translations from "../../components/common/Translations";
import SharedFieldInfoIcon from "../../components/common/FieldInfoIcon";
import ReactSelector from "../../components/common/CommonReactSelector";
import { Spinner } from "../../Spinner";
import { getOrganizationById } from "../../service/Report";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { RiUploadCloud2Line } from "react-icons/ri";
import BackButton from "../../components/common/BackButton";
import CommonButton from "../../components/common/CommonButton";
import { uploadFileToS3 } from "../../service/S3Bucket";
import dummyImg from "../../assets/dummy.webp";
import { resolveAvatarSrc } from "../../utils/avatar";

const FIELD_AGENT_DEFAULT_PASSWORD = "BluField@1234";

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

// Organizations whose field agents carry an Employee Id and a SubContract Id:
// 2 = ONIEC, 24 = Anwar ALmajed.
const EMPLOYEE_ID_ORGANIZATION_IDS = [2, 24];
const isEmployeeIdOrganization = (organization) =>
  EMPLOYEE_ID_ORGANIZATION_IDS.includes(Number(organization));

/** API expects employeeId (number) and SubContract (string) only for these organizations' field agents. */
const isOneicFieldAgent = (values) =>
  values?.userType === "field_agent" && isEmployeeIdOrganization(values?.organization);

const getFieldAgentApiPayload = (values) => {
  if (!isOneicFieldAgent(values)) {
    return {};
  }
  return {
    employeeId: Number(values.employeeId),
    SubContract: String(values.subContract ?? "").trim(),
  };
};

const normalizeIdList = (ids) =>
  Array.isArray(ids)
    ? ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
    : [];

const getSavedUserIdFromResponse = (response) => {
  const value = response?.body?.value;
  if (typeof value === "number" || typeof value === "string") {
    return value;
  }
  return (
    value?.userId ??
    value?.id ??
    response?.body?.userId ??
    response?.userId ??
    null
  );
};

// The "Create group" row shown at the top of the group dropdown, handed to
// ReactSelector as `menuListHeader`. Only super admin can create groups
// (`currentUserCanCreateGroups`); everyone else just picks from the list, and the
// caller renders nothing.
const GroupCreateRow = ({
  groupCreateMode,
  groupCreateValue,
  onStartGroupCreate,
  onChangeGroupCreate,
  onSubmitGroupCreate,
  onCancelGroupCreate,
  createText,
  newGroupPlaceholder,
  cancelLabel,
}) => {
  if (groupCreateMode) {
    return (
      <div className="flex items-center gap-1 px-2 py-2">
        <input
          autoFocus
          value={groupCreateValue}
          onChange={(e) => onChangeGroupCreate?.(e.target.value)}
          onKeyDown={(e) => {
            // Prevent react-select from handling these keystrokes as
            // dropdown navigation / type-ahead so the input works normally.
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
              onSubmitGroupCreate?.();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancelGroupCreate?.();
            }
          }}
          onMouseDown={(e) => e.stopPropagation()}
          placeholder={newGroupPlaceholder || "New group name"}
          className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSubmitGroupCreate?.()}
          aria-label={createText || "Create group"}
          className="rounded-md p-1 text-green-600 hover:bg-green-50"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onCancelGroupCreate?.()}
          aria-label={cancelLabel || "Cancel"}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onStartGroupCreate?.()}
      className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
    >
      <span className="text-base leading-none">＋</span>
      <span>{createText || "Create group"}</span>
    </button>
  );
};

// Info icons on this screen use the shared blue tooltip.
const FieldInfoIcon = (props) => <SharedFieldInfoIcon blueTooltip {...props} />;

const AddUser = ({ userId: userIdProp, isModal = false, onClose, onSuccess }) => {
  const { userId: routeUserId } = useParams();
  const userId = userIdProp ?? routeUserId;
  const fileInputRef = useRef(null);
  // const [organizations, setOrganizations] = useState([]); // was write-only:
  // populated by the commented-out fetchOrganizationsData effect below, but
  // never read anywhere in this component (the "Organization" dropdown
  // actually renders `organization`, the singular state further down).
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOfficeUser, setOfficeUser] = useState(false)
  const [initialUserInfo, setInitialUserInfo] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const { theme, langMode, profilePic } = useContext(AppContext);
  const navigate = useNavigate();
  const [phoneError, setPhoneError] = useState("");
  const [isPasswordCleared, setIsPasswordCleared] = useState(false);
  const [uploadedImg, setUploadedImg] = useState('')
  const [selectedFile, setSelectedFile] = useState('')
  const [signaturePreview, setSignaturePreview] = useState("");
  const [selectedSignatureFile, setSelectedSignatureFile] = useState(null);
  const [organization, setOrganization] = useState([])
  const [department, setDepartment] = useState([])
  const [project, setProject] = useState([])
  const [workflowList, setWorkflowList] = useState([])
  const [roleList, setRoleList] = useState([])
  const [clockIn, setClockIn] = useState([])
  const [groupList, setGroupList] = useState([])
  const [isCreatingGroup, setIsCreatingGroup] = useState(false)
  const [groupMenuOpen, setGroupMenuOpen] = useState(false)
  const [groupCreateMode, setGroupCreateMode] = useState(false)
  const [groupCreateValue, setGroupCreateValue] = useState("")
  const [showPassword, setShowPassword] = useState(false);
  const [isClock, setIsClock] = useState(false)
  const [isLoader, setLoader] = useState(false)
  const [isAllProject, setIsAllProject] = useState(false)
  const [isAllActivity, setIsAllActivity] = useState(false)

  const [initialValues, setInitialValues] = useState({
    name: "",
    userType: "",
    email: "",
    password: "",
    phone: "",
    areaCode: "",
    organization: "",
    departmentList: [],
    projectList: [],
    workflowList: [],
    roleIds: [],
    userStatus: true,
    canAddEditInventory: false,
    canAddEditProtectedAccount: false,
    canTaskManage: false,
    projectIdForClockInClockOut: null,
    employeeId: '', // employed id added by harshit vachhani
    subContract: '', // subContract added by harshit vachhani
    groupId: null, // field-agent group (single select)
    groupList: [], // admin / office user groups (multi select)
    // userVisibility: [],

  });
  const [countryDetail, setCountryDetail] = useState([
    { name: "Afghanistan", length: 9, countryCode: "+93" },
    { name: "Albania", length: 8, countryCode: "+355" },
    { name: "Algeria", length: 9, countryCode: "+213" },
    { name: "Andorra", length: 6, countryCode: "+376" },
    { name: "Angola", length: 9, countryCode: "+244" },
    { name: "Argentina", length: 10, countryCode: "+54" },
    { name: "Armenia", length: 8, countryCode: "+374" },
    { name: "Aruba", length: 7, countryCode: "+297" },
    { name: "Australia", length: 9, countryCode: "+61" },
    { name: "Austria", length: 12, countryCode: "+43" },
    { name: "Azerbaijan", length: 9, countryCode: "+994" },
    { name: "Antigua and Barbuda", length: 7, countryCode: "+1268" },
    { name: "Bahamas", length: 7, countryCode: "+1 242" },
    { name: "Bahrain", length: 8, countryCode: "+973" },
    { name: "Bangladesh", length: 10, countryCode: "+880" },
    { name: "Barbados", length: 10, countryCode: "+1 246" },
    { name: "Belarus", length: 9, countryCode: "+375" },
    { name: "Belgium", length: 9, countryCode: "+32" },
    { name: "Belize", length: 7, countryCode: "+501" },
    { name: "Benin", length: 8, countryCode: "+229" },
    { name: "Bhutan", length: 8, countryCode: "+975" },
    { name: "Bolivia", length: 8, countryCode: "+591" },
    { name: "Bosnia and Herzegovina", length: 8, countryCode: "+387" },
    { name: "Botswana", length: 7, countryCode: "+267" },
    { name: "Brazil", length: 10, countryCode: "+55" },
    { name: "Brunei", length: 7, countryCode: "+673" },
    { name: "Bulgaria", length: 8, countryCode: "+359" },
    { name: "Burkina Faso", length: 10, countryCode: "+226" },
    { name: "Burundi", length: 8, countryCode: "+257" },
    { name: "British Indian Ocean Territiry", length: 7, countryCode: "+246" },
    { name: "Cabo Verde", length: 7, countryCode: "+238" },
    { name: "Cambodia", length: 9, countryCode: "+855" },
    { name: "Cameroon", length: 9, countryCode: "+237" },
    { name: "Canada", length: 11, countryCode: "+1" },
    { name: "Central African Republic", length: 10, countryCode: "+236" },
    { name: "Chad", length: 8, countryCode: "+235" },
    { name: "Chile", length: 9, countryCode: "+56" },
    { name: "China", length: 11, countryCode: "+86" },
    { name: "Colombia", length: 10, countryCode: "+57" },
    { name: "Comoros", length: 7, countryCode: "+269" },
    { name: "Congo (Congo-Brazzaville)", length: 10, countryCode: "+242" },
    { name: "Costa Rica", length: 8, countryCode: "+506" },
    { name: "Croatia", length: 9, countryCode: "+385" },
    { name: "Cuba", length: 8, countryCode: "+53" },
    { name: "Cyprus", length: 8, countryCode: "+357" },
    { name: "Czech Republic", length: 9, countryCode: "+420" },
    { name: "Denmark", length: 8, countryCode: "+45" },
    { name: "Djibouti", length: 7, countryCode: "+253" },
    { name: "Dominica", length: 10, countryCode: "+1 767" },
    { name: "Dominican Republic", length: 10, countryCode: "+1 809" },
    { name: "Ecuador", length: 9, countryCode: "+593" },
    { name: "Egypt", length: 10, countryCode: "+20" },
    { name: "El Salvador", length: 8, countryCode: "+503" },
    { name: "Equatorial Guinea", length: 9, countryCode: "+240" },
    { name: "Eritrea", length: 8, countryCode: "+291" },
    { name: "Estonia", length: 7, countryCode: "+372" },
    { name: "Eswatini", length: 8, countryCode: "+268" },
    { name: "Ethiopia", length: 9, countryCode: "+251" },
    { name: "Fiji", length: 7, countryCode: "+679" },
    { name: "Finland", length: 12, countryCode: "+358" },
    { name: "France", length: 9, countryCode: "+33" },
    { name: "Gabon", length: 8, countryCode: "+241" },
    { name: "Gambia", length: 7, countryCode: "+220" },
    { name: "Georgia", length: 9, countryCode: "+995" },
    { name: "Germany", length: 11, countryCode: "+49" },
    { name: "Ghana", length: 9, countryCode: "+233" },
    { name: "Greece", length: 10, countryCode: "+30" },
    { name: "Grenada", length: 10, countryCode: "+1 473" },
    { name: "Guatemala", length: 8, countryCode: "+502" },
    { name: "Guinea", length: 9, countryCode: "+224" },
    { name: "Guinea-Bissau", length: 7, countryCode: "+245" },
    { name: "Guyana", length: 10, countryCode: "+592" },
    { name: "Haiti", length: 8, countryCode: "+509" },
    { name: "Honduras", length: 8, countryCode: "+504" },
    { name: "Hungary", length: 9, countryCode: "+36" },
    { name: "Iceland", length: 7, countryCode: "+354" },
    { name: "India", length: 10, countryCode: "+91" },
    { name: "Indonesia", length: 10, countryCode: "+62" },
    { name: "Iran", length: 10, countryCode: "+98" },
    { name: "Iraq", length: 10, countryCode: "+964" },
    { name: "Ireland", length: 7, countryCode: "+353" },
    { name: "Israel", length: 9, countryCode: "+972" },
    { name: "Italy", length: 10, countryCode: "+39" },
    { name: "Jamaica", length: 10, countryCode: "+1 876" },
    { name: "Japan", length: 10, countryCode: "+81" },
    { name: "Jordan", length: 9, countryCode: "+962" },
    { name: "Kazakhstan", length: 10, countryCode: "+7" },
    { name: "Kenya", length: 10, countryCode: "+254" },
    { name: "Kiribati", length: 7, countryCode: "+686" },
    { name: "Kuwait", length: 8, countryCode: "+965" },
    { name: "Kyrgyzstan", length: 9, countryCode: "+996" },
    { name: "Laos", length: 8, countryCode: "+856" },
    { name: "Latvia", length: 8, countryCode: "+371" },
    { name: "Lebanon", length: 8, countryCode: "+961" },
    { name: "Lesotho", length: 8, countryCode: "+266" },
    { name: "Liberia", length: 7, countryCode: "+231" },
    { name: "Libya", length: 9, countryCode: "+218" },
    { name: "Liechtenstein", length: 10, countryCode: "+423" },
    { name: "Lithuania", length: 9, countryCode: "+370" },
    { name: "Luxembourg", length: 9, countryCode: "+352" },
    { name: "Madagascar", length: 9, countryCode: "+261" },
    { name: "Malawi", length: 10, countryCode: "+265" },
    { name: "Malaysia", length: 10, countryCode: "+60" },
    { name: "Maldives", length: 7, countryCode: "+960" },
    { name: "Mali", length: 8, countryCode: "+223" },
    { name: "Malta", length: 8, countryCode: "+356" },
    { name: "Marshall Islands", length: 7, countryCode: "+692" },
    { name: "Mauritania", length: 8, countryCode: "+222" },
    { name: "Mauritius", length: 10, countryCode: "+230" },
    { name: "Mexico", length: 10, countryCode: "+52" },
    { name: "Micronesia", length: 7, countryCode: "+691" },
    { name: "Moldova", length: 8, countryCode: "+373" },
    { name: "Monaco", length: 8, countryCode: "+377" },
    { name: "Mongolia", length: 8, countryCode: "+976" },
    { name: "Montenegro", length: 8, countryCode: "+382" },
    { name: "Morocco", length: 10, countryCode: "+212" },
    { name: "Mozambique", length: 9, countryCode: "+258" },
    { name: "Myanmar (formerly Burma)", length: 9, countryCode: "+95" },
    { name: "Namibia", length: 9, countryCode: "+264" },
    { name: "Nauru", length: 7, countryCode: "+674" },
    { name: "Nepal", length: 10, countryCode: "+977" },
    { name: "Netherlands", length: 10, countryCode: "+31" },
    { name: "New Zealand", length: 9, countryCode: "+64" },
    { name: "Nicaragua", length: 8, countryCode: "+505" },
    { name: "Niger", length: 8, countryCode: "+227" },
    { name: "Nigeria", length: 10, countryCode: "+234" },
    { name: "North Korea", length: 9, countryCode: "+850" },
    { name: "North Macedonia", length: 8, countryCode: "+389" },
    { name: "Norway", length: 8, countryCode: "+47" },
    { name: "Oman", length: 8, countryCode: "+968" },
    { name: "Pakistan", length: 10, countryCode: "+92" },
    { name: "Palau", length: 7, countryCode: "+680" },
    { name: "Palestine", length: 10, countryCode: "+970" },
    { name: "Panama", length: 7, countryCode: "+507" },
    { name: "Papua New Guinea", length: 10, countryCode: "+675" },
    { name: "Paraguay", length: 9, countryCode: "+595" },
    { name: "Peru", length: 9, countryCode: "+51" },
    { name: "Philippines", length: 10, countryCode: "+63" },
    { name: "Poland", length: 9, countryCode: "+48" },
    { name: "Portugal", length: 9, countryCode: "+351" },
    { name: "Qatar", length: 8, countryCode: "+974" },
    { name: "Romania", length: 10, countryCode: "+40" },
    { name: "Russia", length: 10, countryCode: "+7" },
    { name: "Rwanda", length: 10, countryCode: "+250" },
    { name: "Saint Kitts and Nevis", length: 10, countryCode: "+1 869" },
    { name: "Saint Lucia", length: 10, countryCode: "+1 758" },
    {
      name: "Saint Vincent and the Grenadines",
      length: 10,
      countryCode: "+1 784",
    },
    { name: "Samoa", length: 7, countryCode: "+685" },
    { name: "San Marino", length: 10, countryCode: "+378" },
    { name: "Sao Tome and Principe", length: 7, countryCode: "+239" },
    { name: "Saudi Arabia", length: 9, countryCode: "+966" },
    { name: "Senegal", length: 9, countryCode: "+221" },
    { name: "Serbia", length: 8, countryCode: "+381" },
    { name: "Seychelles", length: 7, countryCode: "+248" },
    { name: "Sierra Leone", length: 8, countryCode: "+232" },
    { name: "Singapore", length: 8, countryCode: "+65" },
    { name: "Slovakia", length: 9, countryCode: "+421" },
    { name: "Slovenia", length: 8, countryCode: "+386" },
    { name: "Solomon Islands", length: 7, countryCode: "+677" },
    { name: "Somalia", length: 8, countryCode: "+252" },
    { name: "South Africa", length: 10, countryCode: "+27" },
    { name: "South Korea", length: 10, countryCode: "+82" },
    { name: "South Sudan", length: 9, countryCode: "+211" },
    { name: "Spain", length: 9, countryCode: "+34" },
    { name: "Sri Lanka", length: 10, countryCode: "+94" },
    { name: "Sudan", length: 9, countryCode: "+249" },
    { name: "Suriname", length: 7, countryCode: "+597" },
    { name: "Sweden", length: 10, countryCode: "+46" },
    { name: "Switzerland", length: 9, countryCode: "+41" },
    { name: "Syria", length: 9, countryCode: "+963" },
    { name: "Taiwan", length: 9, countryCode: "+886" },
    { name: "Tajikistan", length: 9, countryCode: "+992" },
    { name: "Tanzania", length: 10, countryCode: "+255" },
    { name: "Thailand", length: 9, countryCode: "+66" },
    { name: "Timor-Leste", length: 9, countryCode: "+670" },
    { name: "Togo", length: 8, countryCode: "+228" },
    { name: "Tonga", length: 7, countryCode: "+676" },
    { name: "Trinidad and Tobago", length: 10, countryCode: "+1 868" },
    { name: "Tunisia", length: 8, countryCode: "+216" },
    { name: "Turkey", length: 10, countryCode: "+90" },
    { name: "Turkmenistan", length: 8, countryCode: "+993" },
    { name: "Tuvalu", length: 7, countryCode: "+688" },
    { name: "Uganda", length: 10, countryCode: "+256" },
    { name: "Ukraine", length: 9, countryCode: "+380" },
    { name: "United Arab Emirates", length: 9, countryCode: "+971" },
    { name: "United Kingdom", length: 10, countryCode: "+44" },
    { name: "United States", length: 10, countryCode: "+1" },
    { name: "Uruguay", length: 8, countryCode: "+598" },
    { name: "Uzbekistan", length: 9, countryCode: "+998" },
    { name: "Vanuatu", length: 7, countryCode: "+678" },
    { name: "Vatican City", length: 10, countryCode: "+379" },
    { name: "Venezuela", length: 10, countryCode: "+58" },
    { name: "Vietnam", length: 10, countryCode: "+84" },
    { name: "Yemen", length: 9, countryCode: "+967" },
    { name: "Zambia", length: 10, countryCode: "+260" },
    { name: "Zimbabwe", length: 9, countryCode: "+263" },
  ]);
  const [selectedUserType, setSelectedUserType] = useState("");
  const clockInOptions = Array.isArray(clockIn)
    ? clockIn
        .filter((item) => item?.id !== null && item?.id !== undefined)
        .map((item) => ({ value: item.id, label: item.name }))
    : [];
  const hasClockInOptions = clockInOptions.length > 0;

  const tr = translations[langMode];
  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required((tr.userFormNameRequired ?? "Full Name is required")),
    email: Yup.string()
      .trim()
      .required((tr.userFormEmailRequired ?? "Email is required")),
    phone: Yup.string()
      .transform((value) => (value ? value.replace(/\D/g, "") : value))
      .required((tr.userFormPhoneRequired ?? "Phone number is required")),
    userType: Yup.string().required((tr.userFormUserTypeRequired ?? "User Type is required")),
    organization: Yup.mixed()
      .test(
        "organization-required",
        (tr.userFormOrgRequired ?? "Organization is required"),
        (value) => value !== "" && value !== null && value !== undefined
      ),
    departmentList: Yup.array().test(
      "department-required",
      (tr.userFormDepartmentRequired ?? "Department is required"),
      (value) => (Array.isArray(value) && value.length > 0)
    ),
    projectList: Yup.array().test(
      "project-required",
      (tr.userFormProjectRequired ?? "Project is required"),
      (value) => isAllProject || (Array.isArray(value) && value.length > 0)
    ),
    workflowList: Yup.array().test(
      "activity-required",
      (tr.userFormActivityRequired ?? "Activity is required"),
      (value) => isAllActivity || (Array.isArray(value) && value.length > 0)
    ),
    projectIdForClockInClockOut: Yup.mixed()
      .nullable()
      .test(
        "clockin-required",
        (tr.userFormClockInRequired ?? "Clock In is required"),
        (value) =>
          !isClock ||
          !hasClockInOptions ||
          (value !== null && value !== undefined && value !== "")
      ),
    employeeId: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? undefined : value
      )
      .when(["userType", "organization"], {
        is: (userType, organization) =>
          userType === "field_agent" && isEmployeeIdOrganization(organization),
        then: (schema) =>
          schema
            .required((tr.userFormFieldRequired ?? "{field} is required").replace("{field}", tr.employee))
            .typeError((tr.userFormFieldMustBeNumber ?? "{field} must be a number").replace("{field}", tr.employee)),
        otherwise: (schema) => schema.notRequired(),
      }),
    subContract: Yup.string().when(["userType", "organization"], {
      is: (userType, organization) =>
        userType === "field_agent" && isEmployeeIdOrganization(organization),
      then: (schema) =>
        schema
          .trim()
          .required((tr.userFormFieldRequired ?? "{field} is required").replace("{field}", tr.subContract)),
      otherwise: (schema) => schema.notRequired(),
    }),
    // Field agents get a static temporary password, so no password validation.
    password: Yup.string().when("userType", {
      is: (userType) => userType === "field_agent",
      then: (schema) => schema.notRequired(),
      otherwise: () =>
        userId
          ? Yup.string()
            .transform((value) => (value === "" ? undefined : value))
            .optional()
            .min(8, (tr.userFormPasswordMin ?? "Password must be at least 8 characters"))
            .matches(/[A-Z]/, (tr.userFormPasswordUpper ?? "Password must contain at least one uppercase letter"))
            .matches(/[a-z]/, (tr.userFormPasswordLower ?? "Password must contain at least one lowercase letter"))
            .matches(/\d/, (tr.userFormPasswordNumber ?? "Password must contain at least one number"))
            .matches(
              /[!@#$%^&*(),.?":{}|<>]/,
              (tr.userFormPasswordSpecial ?? "Password must contain at least one special character")
            )
          : Yup.string()
            .required((tr.userFormPasswordRequired ?? "Password is required"))
            .min(8, (tr.userFormPasswordMin ?? "Password must be at least 8 characters"))
            .matches(/[A-Z]/, (tr.userFormPasswordUpper ?? "Password must contain at least one uppercase letter"))
            .matches(/[a-z]/, (tr.userFormPasswordLower ?? "Password must contain at least one lowercase letter"))
            .matches(/\d/, (tr.userFormPasswordNumber ?? "Password must contain at least one number"))
            .matches(
              /[!@#$%^&*(),.?":{}|<>]/,
              (tr.userFormPasswordSpecial ?? "Password must contain at least one special character")
            ),
    }),
  });

  const GetUserInfo = () => {
    getUserInfoAPI(userId)
      .then((response) => {
        const user = response.data.body.value;
        setUserInfo(user);
        setInitialUserInfo(user);
        setIsPasswordCleared(false);
        setSignaturePreview(user?.signature || "");
        setSelectedSignatureFile(null);
        setInitialValues({
          name: user.name || "",
          userType: user.userType || "",
          email: user.email || "",
          phone: user?.phone?.replace(/\D/g, "")?.startsWith("968")
            ? `${user?.phone?.replace(/\D/g, "")?.slice(3, 7)} ${user?.phone?.replace(/\D/g, "")?.slice(7, 11)}`
            : "",
          areaCode: user.areaCode || "",
          workflowList: normalizeIdList(user.workflowList),
          userStatus: user.userStatus || false,
          organization: user.organization.organizationId || "",
          password: "",
          departmentList: Array.isArray(user.departmentList)
            ? user.departmentList.map((id) => String(id))
            : [],
          projectList: Array.isArray(user.projectList)
            ? user.projectList.map((id) => String(id))
            : [],
          roleIds: user.roleIds || [],
          projectIdForClockInClockOut: user?.projectIdForClockInClockOut || null,
          employeeId: user?.employeeId ?? "",
          subContract: user?.SubContract ?? user?.subContract ?? "",
          groupId: user?.groupId ?? null,
          groupList: Array.isArray(user.groupList)
            ? user.groupList.map((id) => String(id))
            : [],
        });
        setIsAllProject(!Array.isArray(user.projectList) || user.projectList.length === 0);
        setIsAllActivity(!Array.isArray(user.workflowList) || user.workflowList.length === 0);
        setIsClock(user?.clockInEnabled);
        setUploadedImg(user?.profilePic || "");
        setSelectedFile(user?.profilePic || "");
      })
      .catch((error) => {
        console.error("An error occurred:", error);
      });
  };

  useEffect(() => {
    if (userId) {
      GetUserInfo();
    }
  }, [userId]);


  const userDetails = JSON.parse(localStorage.getItem("userDetails"))
  console.log('userDetails: ', userDetails);

  // const fetchOrganizationsData = async () => {
  //   try {
  //     let orgs;
  //     if (userDetails.userType === "super_admin") {
  //       orgs = await fetchOrganizations();
  //     } else {
  //       const fetchedOrg = await getOrganizationById(userDetails?.organization?.organizationId);
  //       orgs = Array.isArray(fetchedOrg) ? fetchedOrg : [fetchedOrg];
  //     }
  //     setOrganizations(orgs);
  //   } catch (error) { }
  // };
  //
  // useEffect(() => {
  //   fetchOrganizationsData();
  // }, []);
  // Removed as a duplicate: another effect further down (~line 800) fetches
  // the same organization list into `organization` (singular), which is the
  // state actually rendered in the Organization dropdown -- this one's result
  // (`organizations`, plural) was never read anywhere.

  useEffect(() => {
    const userType = Cookies.get("userType");
    setIsSuperAdmin(userType === "super_admin");
    setIsAdmin(userType === "admin");
    setOfficeUser(userType === 'office_user')
  });

  console.log("Selected User Type:", selectedUserType);
  console.log("isSuperAdmin:", isSuperAdmin);
  console.log("isAdmin:", isAdmin);
  console.log("isOfficeUser:", isOfficeUser);


  const handleGoBack = () => {
    if (isModal) {
      onClose?.();
      return;
    }
    navigate("/all-users");
  };

  const validateImageFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/jpg"];

    if (!file || !allowedTypes.includes(file.type)) {
      toast.error(translations[langMode].invalidfiletype);
      return false;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(translations[langMode].maxFileSize5MB || "Max file size: 5MB");
      return false;
    }

    return true;
  };


  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 8);
    const match = cleaned.match(/^(\d{0,4})(\d{0,4})$/);
    if (!match) return "";

    const [, part1, part2] = match;
    return `${part1}${part2 ? " " + part2 : ""}`;
  };


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!userId && values.userType !== "field_agent" && !values.password) {
        toast.error((translations[langMode].userFormPasswordRequired ?? "Password is required"));
        return;
      }

      const token = Cookies.get("accessToken");
      if (!token) {
        toast.error((translations[langMode].userFormTokenMissing ?? "Token is not present"));
        return;
      }

      try {
        let response;
        const isFieldAgent = values.userType === "field_agent";
        const isAdminOrOffice =
          values.userType === "admin" || values.userType === "office_user";
        // Groups apply to admin / office users (multi-select); sent as numeric ids.
        const groupIdsPayload = isAdminOrOffice && Array.isArray(values.groupList)
          ? values.groupList.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
          : [];
        let signature = isFieldAgent ? signaturePreview : "";
        let profilePic = uploadedImg ? await uploadFileToS3(selectedFile) : null;
        setLoader(true)

        if (isFieldAgent && selectedSignatureFile) {
          signature = await uploadFileToS3(selectedSignatureFile);
          if (!signature) {
            throw new Error("Signature upload failed");
          }
        }

        if (userId) {
          const workflowIdsEdit = normalizeIdList(values.workflowList);
          const roleIdsEdit = Array.isArray(values.roleIds)
            ? values.roleIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
            : [];
          const {
            subContract: _subContract,
            employeeId: _employeeId,
            groupList: _groupList,
            ...updateValues
          } = values;
          response = await updateUser(
            {
              userId: Number(userId),
              ...updateValues,
              ...getFieldAgentApiPayload(values),
              projectIdForClockInClockOut: isClock ? values?.projectIdForClockInClockOut : null,
              phone: `+968 ${values?.phone}`,
              workflowList: workflowIdsEdit,
              roleIds: roleIdsEdit,
              // Field agents can never set/change a password from this screen.
              password: isFieldAgent ? undefined : values.password || undefined,
              passwordChanged: isFieldAgent ? false : isPasswordCleared,
              organization: { organizationId: values.organization },
              ...(isFieldAgent && signature ? { signature } : {}),
              ...(profilePic ? { profilePic } : {}),
              ...values?.userType === "field_agent" && { clockInEnabled: isClock },
              // Group only applies to field agents.
              groupId: isFieldAgent ? (values.groupId ?? null) : undefined,
              // Group list applies to admin / office users (multi-select).
              groupList: isAdminOrOffice ? groupIdsPayload : undefined,
            },
            token
          );
        } else {
          const departmentIds = Array.isArray(values.departmentList)
            ? values.departmentList.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
            : [];
          const projectIds = Array.isArray(values.projectList)
            ? values.projectList.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
            : [];
          const workflowIds = normalizeIdList(values.workflowList);
          const roleIdsPayload = Array.isArray(values.roleIds)
            ? values.roleIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
            : [];
          response = await createUser(
            {
              name: values.name,
              email: values.email,
              phone: `+968 ${values?.phone}`,
              userType: values.userType,
              areaCode: Number(values.areaCode) || 0,
              userStatus: Boolean(values.userStatus),
              organization: { organizationId: Number(values.organization) },
              departmentList: departmentIds,
              projectList: projectIds,
              workflowList: workflowIds,
              roleIds: roleIdsPayload,
              password: isFieldAgent
                ? FIELD_AGENT_DEFAULT_PASSWORD
                : values.password,
              ...(isFieldAgent ? { firstTimeLogin: true } : {}),
              ...(isFieldAgent && signature ? { signature } : {}),
              ...(profilePic ? { profilePic } : {}),
              ...getFieldAgentApiPayload(values),
              // Group only applies to field agents.
              ...(isFieldAgent ? { groupId: values.groupId ?? null } : {}),
              // Group list applies to admin / office users (multi-select).
              ...(isAdminOrOffice ? { groupList: groupIdsPayload } : {}),
            },
            token
          );
        }
        setLoader(false)
        if (response.header.code === 600) {
          const savedUserId = userId || getSavedUserIdFromResponse(response);
          const savedUserContext = {
            userId: savedUserId ? Number(savedUserId) : null,
            mode: userId ? "edit" : "create",
            values: {
              ...values,
              phone: `+968 ${values?.phone}`,
            },
            response,
          };
          if (userId) {
            toast.success(translations[langMode].userupdatedsuc);
            if (isModal) {
              onSuccess?.(savedUserContext);
            } else {
              navigate("/all-users");
            }
          } else {
            toast.success(translations[langMode].usercreatedSuc);
            resetForm();
            setSignaturePreview("");
            setSelectedSignatureFile(null);
            setInitialValues((prev) => ({ ...prev }));
            if (isModal) {
              onSuccess?.(savedUserContext);
            } else {
              navigate("/all-users");
            }
          }
        } else if (response.header.code === 650) {
          const backendMessage =
            response?.body?.value ??
            response?.body?.error ??
            response?.header?.message;
          if (backendMessage) {
            toast.error(backendMessage);
          }
        } else if (response.header.code === 612) {
          toast.error(response.body.value);
        } else {
          toast.error(response.body.value);
        }
      } catch (error) {
        setLoader(false)
        console.error("Error handling user:", error);
        const backendMessage = getBackendErrorMessage(error);
        if (backendMessage) {
          toast.error(backendMessage);
        }
      } finally {
        setLoader(false)
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    formik.validateForm();
  }, [isAllProject, isAllActivity, isClock]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!validateImageFile(file)) {
      return;
    }

    setUploadedImg(URL.createObjectURL(file));
    setSelectedFile(file); // Store file for later upload
  };

  const handleSignatureUpload = (event) => {
    const file = event.target.files[0];
    if (!validateImageFile(file)) {
      return;
    }

    setSignaturePreview(URL.createObjectURL(file));
    setSelectedSignatureFile(file);
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        let data;
        if (userDetails?.userType === "super_admin") {
          data = await getAllByOrganization();
        } else {
          const fetchedOrg = await getOrganizationById(userDetails?.organization?.organizationId);
          data = Array.isArray(fetchedOrg) ? fetchedOrg : [fetchedOrg];
        }
        setInitialValues((prevValues) => ({
          ...prevValues,
        }));
        setOrganization(data);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!formik?.values?.organization) return; // Don't fetch if there's no organization selected
      try {
        const data = await getAllByOrganizationId(formik?.values?.organization);
        setDepartment(data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchDepartments(); // Trigger the department fetch whenever `userInfo.organization` changes
  }, [formik?.values?.organization]);


  useEffect(() => {
    const fetchProjectList = async () => {
      if (!formik?.values?.departmentList?.length > 0) return; // Don't fetch if there's no organization selected

      try {
        const selectedDeptIds = (formik?.values?.departmentList || []).map((id) => String(id));
        const deptIdsForApi = selectedDeptIds.includes("all")
          ? (department || [])
            .map((i) => Number(i?.id))
            .filter((n) => !Number.isNaN(n))
          : selectedDeptIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n));

        const data = await getMultipleProject(deptIdsForApi);
        setProject(data)
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchProjectList(); // Trigger the department fetch whenever `userInfo.organization` changes
  }, [formik?.values?.departmentList]);

  // Field agents pick a group that belongs to their (single) selected department.
  const selectedGroupDepartmentId = formik?.values?.userType === "field_agent"
    ? formik?.values?.departmentList?.[0]
    : undefined;

  useEffect(() => {
    const fetchGroups = async () => {
      const ut = formik?.values?.userType;
      const depts = Array.isArray(formik?.values?.departmentList)
        ? formik.values.departmentList
        : [];
      // Field agents use a single department; admin / office users can span all their departments.
      let deptIds = [];
      if (ut === "field_agent") {
        deptIds = depts.slice(0, 1);
      } else if (ut === "admin" || ut === "office_user") {
        deptIds = depts;
      }
      const validIds = Array.from(
        new Set(deptIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n)))
      );
      if (!validIds.length) {
        setGroupList([]);
        return;
      }
      try {
        const results = await Promise.all(
          validIds.map((id) => getGroupsByDepartment(id))
        );
        // Merge groups from every selected department, de-duplicated by group id.
        const seen = new Set();
        const merged = [];
        results.forEach((list) => {
          (Array.isArray(list) ? list : []).forEach((g) => {
            const id = g?.id ?? g?.groupId ?? g?.groupByDepartmentId;
            if (id != null && !seen.has(id)) {
              seen.add(id);
              merged.push(g);
            }
          });
        });
        // Only show groups the logged-in user has access to (userDetails.groupList);
        // a blank access list means no restriction.
        setGroupList(filterGroupsByUserAccess(merged));
      } catch (error) {
        console.error("Error fetching groups:", error);
        setGroupList([]);
      }
    };
    fetchGroups();
  }, [formik?.values?.userType, formik?.values?.departmentList]);

  useEffect(() => {
    const fetchRoles = async () => {
      if (!formik?.values?.organization) {
        setRoleList([]);
        return;
      }
      try {
        const data = await getRoleList(formik.values.organization);
        setRoleList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching roles:", error);
        setRoleList([]);
      }
    };
    fetchRoles();
  }, [formik?.values?.organization]);

  useEffect(() => {
    const fetchActivityList = async () => {
      try {
        const data = await getMultipleActivity(formik?.values?.projectList);
        setWorkflowList(data)

        const clockId = await projectsForClockIn(formik?.values?.projectList)
        setClockIn(clockId)
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchActivityList(); // Trigger the project fetch whenever `userInfo.organization` changes
  }, [formik?.values?.projectList]);

  const [isLoading, setIsLoading] = useState(true);
  const showFieldAgentIdentityFields = isOneicFieldAgent(formik?.values);
  // Group access of the logged-in user:
  //  - super admin: always sees the group field and can create groups
  //  - admin / office user: sees the group field only when they have at least
  //    one group assigned, and can never create groups
  const canUseGroups = currentUserCanUseGroups();
  const canCreateGroup = currentUserCanCreateGroups();

  useEffect(() => {
    const userTypes = ["admin", "supervisor", "office_user", "field_agent"];
    const userType = userTypes.find((item) => item === formik.values.userType);
    if (userType) {
      setSelectedUserType({ value: userType, label: userType });
    }
    setTimeout(() => {
      setIsLoading(false); // Stop loading after delay
    }, 3000); // 2-second delay
  }, [formik.values.userType]);

  // organization
  const selectedOrganization = Array.isArray(organization) && (organization || [])?.length > 0 ? (organization || [])?.find((org) => org.organizationId === formik.values.organization) : {};

  const formattedOrganization = selectedOrganization
    ? { value: selectedOrganization.organizationId, label: selectedOrganization.organizationName }
    : null;

  // department
  // Validation messages already on screen are re-generated in the new language.
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langMode]);

  const selectedDepartments = (department || []).filter((d) =>
    (formik.values.departmentList || []).map(String).includes(String(d.id))
  );

  const formattedDepartment =
    selectedDepartments.length > 0
      ? department.length === (formik.values.departmentList || []).length
        ? { value: "all", label: translations[langMode].all ?? "All" }
        : selectedDepartments.map((d) => ({ value: String(d.id), label: d.name }))
      : null;


  // Project
  const selectedProject = (Object.values(project)?.flat() || [])?.filter((p) =>
    (formik.values.projectList || []).map(String).includes(String(p.id))
  );

  const formattedProject = selectedProject
    ? Object.values(project)?.flat().length > 0 && (Object.values(project)?.flat().length === formik.values.projectList.length)
      ? { value: "all", label: translations[langMode].all ?? "All" }
      : (selectedProject || [])?.map(item => ({ value: String(item.id), label: item.name }))
    : null;

  // clock in
  const formattedClockIn = clockInOptions.find(
    (item) => item.value === formik.values.projectIdForClockInClockOut
  );

  // Roles
  const selectedRoles = (roleList || []).filter((r) =>
    (formik?.values?.roleIds || []).includes(r?.id ?? r?.roleId)
  );
  const formattedRoles = selectedRoles.length
    ? selectedRoles.map((r) => ({ value: r?.id ?? r?.roleId, label: r?.name ?? r?.roleName ?? "-" }))
    : null;

  // Activity
  const selectedWorkflowIds = (formik?.values?.workflowList || []).map(String);
  const selectedAllActivity = workflowList?.length > 0 && selectedWorkflowIds.length === workflowList?.length;
  const selectedActivity = workflowList?.filter((org) => selectedWorkflowIds.includes(String(org.workflowId)));

  const formattedActivity =
    selectedAllActivity ? { value: "all", label: translations[langMode].all ?? "All" } :
      selectedActivity
        ? (selectedActivity || [])?.map(item => ({ value: item.workflowId, label: item.workflowName }))
        : null;
  // The multi selects below are controlled from the form values, so picking "All" leaves only the
  // "All" chip (the individual picks drop out) instead of piling both onto the field.
  const asMultiValue = (value) => (Array.isArray(value) ? value : value ? [value] : []);
  // Whether every item is currently selected, i.e. the field shows the single "All" chip.
  const allDepartmentsSelected =
    (department || []).length > 0 && (department || []).length === (formik.values.departmentList || []).length;
  const selectedAllProject =
    (Object.values(project)?.flat() || []).length > 0 &&
    (Object.values(project)?.flat() || []).length === (formik.values.projectList || []).length;
  // While "All" is on, every option in the menu shows ticked.
  const getOptionSelectedWhenAll = (allOn) => (option, selectValue) =>
    Boolean(allOn) || (selectValue || []).some((item) => String(item.value) === String(option.value));
  // Unticking one item while "All" is on turns "All" off and keeps every other item selected.
  // Returns those remaining ids, or null when the change wasn't an untick of a single item.
  const getIdsAfterUntick = (actionMeta, allIds) => {
    const untickedId = actionMeta?.option?.value ?? actionMeta?.removedValue?.value;
    if (actionMeta?.action !== "deselect-option" || untickedId === undefined || untickedId === "all") {
      return null;
    }
    return allIds.filter((id) => String(id) !== String(untickedId));
  };
  // Group (field-agent only) — normalize whatever id/name shape the API returns
  const getGroupId = (g) => g?.id ?? g?.groupId ?? g?.groupByDepartmentId;
  const getGroupName = (g) => g?.groupName ?? g?.name ?? "-";
  const groupOptions = (groupList || []).map((g) => ({
    value: getGroupId(g),
    label: getGroupName(g),
  }));
  const selectedGroupOption =
    groupOptions.find((o) => o.value === formik?.values?.groupId) || null;

  // Group select (admin / office user) — single group, stored in groupList as a one-item id array.
  const selectedGroupListOption =
    (groupList || [])
      .map((g) => ({ value: getGroupId(g), label: getGroupName(g) }))
      .find((o) => (formik?.values?.groupList || []).map(String).includes(String(o.value))) ||
    null;

  const handleCreateGroup = async (inputValue) => {
    const departmentId = formik?.values?.departmentList?.[0];
    if (!departmentId) {
      toast.error(translations[langMode]?.plxselectanoption ?? "Select a department first");
      return;
    }
    const name = String(inputValue || "").trim();
    if (!name) return;
    try {
      setIsCreatingGroup(true);
      const created = await createGroup({ groupName: name, departmentId: Number(departmentId) });
      // Refetch so the list reflects the backend, then select the newly created group
      const refreshed = await getGroupsByDepartment(Number(departmentId));
      const list = Array.isArray(refreshed) ? refreshed : [];
      setGroupList(list);
      const newId =
        getGroupId(created) ??
        getGroupId(list.find((g) => getGroupName(g)?.toLowerCase() === name.toLowerCase()));
      if (newId != null) {
        formik.setFieldValue("groupId", newId);
      }
      toast.success(translations[langMode]?.groupCreated ?? "Group created");
    } catch (error) {
      console.error("Error creating group:", error);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // Inline "Create group" flow shown as the first row of the group dropdown menu.
  const startGroupCreate = () => {
    setGroupCreateValue("");
    setGroupCreateMode(true);
    setGroupMenuOpen(true);
  };
  const cancelGroupCreate = () => {
    setGroupCreateMode(false);
    setGroupCreateValue("");
  };
  const submitGroupCreate = async () => {
    const name = groupCreateValue.trim();
    if (!name) return;
    await handleCreateGroup(name);
    setGroupCreateMode(false);
    setGroupCreateValue("");
    setGroupMenuOpen(false);
  };

  const profileImageSrc = resolveAvatarSrc({
    uploadedImg,
    userProfilePic: userInfo?.profilePic,
    contextProfilePic: profilePic,
    dummyImg,
    mode: userId ? "edit" : "create",
  });
  const signatureFileLabel = selectedSignatureFile?.name || (signaturePreview ? translations[langMode].userFormSignatureReady : "");
  const passwordValue = formik?.values?.password || "";
  const passwordChecklist = [
    { label: (translations[langMode].userFormPwMin ?? "At least 8 characters"), valid: passwordValue.length >= 8 },
    { label: (translations[langMode].userFormPwUpper ?? "One uppercase letter"), valid: /[A-Z]/.test(passwordValue) },
    { label: (translations[langMode].userFormPwLower ?? "One lowercase letter"), valid: /[a-z]/.test(passwordValue) },
    { label: (translations[langMode].userFormPwNumber ?? "One number"), valid: /\d/.test(passwordValue) },
    {
      label: (translations[langMode].userFormPwSpecial ?? "One special character"),
      valid: /[!@#$%^&*(),.?":{}|<>]/.test(passwordValue),
    },
  ];
  const showPasswordChecklist = !userId || Boolean(passwordValue);
  const passwordChecklistClass = isModal && userId
    ? `absolute left-0 right-0 top-full z-20 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1 rounded-md border border-[#D9E6F5] bg-white px-2 py-1 shadow-sm ${theme === "dark"
      ? "dark-mode"
      : theme === "high-contrast"
        ? "high-contrast"
        : ""
    }`
    : "mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1";

  const pageContainerClass = isModal
    ? "w-full bg-white flex flex-col gap-0 px-4 sm:px-8 lg:px-12 pt-8 sm:pt-10 pb-0 min-w-0"
    : "w-full min-w-0 max-h-[93vh] h-full bg-white flex flex-col gap-6 sm:gap-[38px]";
  const formContainerClass = isModal
    ? `w-full max-w-5xl mx-auto ${theme === "dark"
      ? "dark-mode"
      : theme === "high-contrast"
        ? "high-contrast"
        : ""
    }`
    : `w-full sm:w-[90%] md:w-[80%] lg:w-[75%] mx-auto ${theme === "dark"
      ? "dark-mode"
      : theme === "high-contrast"
        ? "high-contrast"
        : ""
    } `;
  const selectorMenuProps = isModal
    ? {
      autoScrollMenuIntoView: true,
      maxMenuHeight: 220,
    }
    : {};
  const formContentClass = isModal ? "pb-6" : "";
  const actionFooterClass = isModal
    ? `sticky bottom-0 z-30 -mx-4 sm:-mx-8 lg:-mx-12 mt-6 px-4 py-4 sm:px-8 sm:py-5 lg:px-12 border-t border-gray-100 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "bg-white"}`
    : "mt-0";
  const actionButtonRowClass = isModal
    ? "flex flex-wrap justify-end gap-[16px] items-center"
    : "pt-[39px] flex justify-center gap-[16px] items-center";

  return (
    <>

      <div className={pageContainerClass}>

        {isModal ? (
          <h2
            className={`sticky top-0 z-10 -mx-4 -mt-8 px-4 pb-3 pt-8 text-base font-semibold uppercase text-gray-800 pe-12 sm:-mx-8 sm:-mt-10 sm:px-8 sm:pt-10 lg:-mx-12 lg:px-12 ${
              theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "bg-white"
            }`}
          >
            {userId ? translations[langMode].userFormEditUser : translations[langMode].addUser}
          </h2>
        ) : (
          <BackButton
            path={'/all-users'}
            pagename={translations[langMode].addUser}
          />
        )}
        <div className={formContainerClass}>
          <div className="w-full">
            <div className="flex justify-between items-end">
              <div className="mb-5 flex flex-wrap gap-8">
                <div>
                  <span
                    className="text-base text-[#111827] font-semibold"
                  >
                    <p className="">{translations[langMode].profileImage}</p>
                  </span>
                  <div className="relative w-[150px] h-[150px] mt-4 mb-[24px]">
                    <img
                      key={profilePic}
                      className={`w-[150px] h-[150px] p-1 rounded-full border-2 border-[#236DB4] ${theme === "dark"
                        ? "bg-white"
                        : theme === "high-contrast"
                          ? "bg-white"
                          : ""
                        }`}
                      src={profileImageSrc}
                      alt="profileimg"
                    />
                    <button
                      className="absolute bottom-2 right-2 bg-white p-1 rounded-full shadow-md border border-gray-300"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <img src='https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344927/uploadImage_rihjhv.png' alt="profile" className="w-[30px] h-[30px] text-gray-600" />
                    </button>

                  </div>
                  <input
                    type="file"
                    name="media"
                    onChange={handleImageUpload}
                    accept="image/jpeg, image/png, image/gif, image/jpg"
                    className="hidden"
                    ref={fileInputRef}
                  />
                </div>
              </div>
              {formik?.values?.userType === "field_agent" && <div className="flex gap-2 items-center">
                <input type="checkbox" data-testid="clockin-checkbox" checked={isClock} onClick={() => setIsClock(!isClock)} />
                {translations[langMode].isClockin}
              </div>}
            </div>

            <form onSubmit={formik.handleSubmit}>
              <React.Fragment>
                <div className={formContentClass}>
                  <div className="grid xl:grid-cols-3 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-2 grid-cols-1 gap-4 mb-[16px]">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm flex items-center gap-2">
                        <span>{translations[langMode].fullName}</span>
                        <FieldInfoIcon tooltipText={translations[langMode].fullNameInfo} />
                        <span className="text-[#FF9F29]">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        data-testid="full-name-input"
                        // component={CommonInput
                        // Field}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`block p-3 w-full text-sm text-gray-900 bg-transparent rounded-[10px] border-1 border-[#D1D5DB]
                    appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 peer ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "high-contrast"
                              : ""
                          }`}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <p className="text-xs text-red-500">{formik.errors.name}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm flex items-center gap-2">
                        <span>{translations[langMode].userEmail}</span>
                        <FieldInfoIcon tooltipText={translations[langMode].userEmailInfo} />
                        <span className="text-[#FF9F29]">*</span>
                      </label>
                      <input
                        type="text"
                        name="email"
                        data-testid="username-email"
                        label={translations[langMode].Email}
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`block p-3 w-full text-sm text-gray-900 bg-transparent rounded-[10px] border-1 border-[#D1D5DB]
                    appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 peer ${theme === "dark"
                            ? "dark-mode"
                            : theme === "high-contrast"
                              ? "high-contrast"
                              : ""
                          }`}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <p className="text-xs text-red-500">{formik.errors.email}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm flex items-center gap-2">
                        <span>{translations[langMode].Phone}</span>
                        <FieldInfoIcon tooltipText={translations[langMode].phoneInfo} />
                        <span className="text-[#FF9F29]">*</span>
                      </label>
                      <div className="flex items-center rounded-[10px] w-full bg-transparent py-[5px] px-[10px] border border-[#D1D5DB] text-sm">
                        <div className="flex items-center gap-2">
                          <span>🇴🇲</span>
                          <span className="font-bold">+968</span>
                        </div>
                        <input
                          className={`block p-2 w-full text-sm text-gray-900 bg-transparent rounded-md
                            appearance-none border-0 focus:ring-0 focus:!shadow-none peer ${theme === "dark"
                              ? "dark-mode"
                              : theme === "high-contrast"
                                ? "high-contrast"
                                : ""
                            }`}
                          type="tel"
                          name="phone"
                          data-testid="phone"
                          value={formik.values.phone}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const cleaned = raw.replace(/\D/g, "");
                            const formatted = formatPhoneNumber(cleaned);
                            formik.setFieldValue("phone", formatted);
                          }}
                          onBlur={formik.handleBlur}
                          placeholder="9123 4567"
                          maxLength={9}
                        />
                      </div>
                      {formik.touched.phone && formik.errors.phone && (
                        <p className="text-xs text-red-500">{formik.errors.phone}</p>
                      )}
                    </div>
                    {isLoading ? (
                      <Spinner />
                    ) : (
                      <>
                        <div className="flex flex-col gap-2">
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].userType}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].userTypeInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <ReactSelector
                            {...selectorMenuProps}
                            dataTestId="user-type-select"
                            name="userType"
                            height="46px"
                            defaultValue={selectedUserType}
                            options={["admin", "supervisor", "office_user", "field_agent"]
                              .filter((item) => {
                                const creator = userDetails.userType;
                                // Supervisors can only be created by super admin.
                                if (item === "supervisor")
                                  return creator === "super_admin";
                                // Admins cannot be created by admins or supervisors.
                                if (item === "admin")
                                  return !(creator === "admin" || creator === "supervisor");
                                return true;
                              })
                              .map(item => ({
                                value: item,
                                label: translations[langMode][item] ?? item,
                              }))}
                            placeholder={translations[langMode].selectusertpye}
                            onChange={(e) => {
                              const value = e.value;
                              setSelectedUserType(e);
                              if (value !== "field_agent") {
                                formik.setFieldValue("userType", value);
                                setIsClock(false)
                              } else {
                                // Field agents should not have roles assigned from this screen.
                                formik.setValues({ ...formik.values, userType: value, roleIds: [] });
                              }
                            }}
                            onBlur={() => formik.setFieldTouched("userType", true)}
                          />
                          {formik.touched.userType && formik.errors.userType && (
                            <p className="text-xs text-red-500">{formik.errors.userType}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].org}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].orgInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <ReactSelector
                            {...selectorMenuProps}
                            dataTestId="organization-select"
                            height="46px"
                            options={
                              Array.isArray(organization)
                                ? (organization || [])?.map((item) => ({
                                  value: item.organizationId,
                                  label: item.organizationName,
                                }))
                                : []
                            }
                            defaultValue={formattedOrganization}
                            onChange={(e) => {
                              formik.setValues({
                                ...formik.values,
                                organization: e?.value,
                                roleIds: [],
                                departmentList: [],
                                projectList: [],
                                workflowList: [],
                                projectIdForClockInClockOut: null,
                                groupId: null,
                              });
                              setIsAllProject(false);
                              setIsAllActivity(false);
                              setProject([]);
                              setWorkflowList([]);
                              setClockIn([]);
                              setGroupList([]);
                              setGroupCreateMode(false);
                              setGroupCreateValue("");
                              setGroupMenuOpen(false);
                            }}
                            onBlur={() => formik.setFieldTouched("organization", true)}
                            placeholder={translations[langMode].selectOrg}
                          />
                          {formik.touched.organization && formik.errors.organization && (
                            <p className="text-xs text-red-500">{formik.errors.organization}</p>
                          )}
                        </div>
                        {formik?.values?.userType !== "field_agent" && (
                          <div className="flex flex-col gap-2">
                            <label className="text-sm flex items-center gap-2">
                              <span>{translations[langMode].roleName || "Roles"}</span>
                              <FieldInfoIcon tooltipText={translations[langMode].rolesInfo} />
                            </label>
                            <ReactSelector
                              {...selectorMenuProps}
                              dataTestId="role-select"
                              key={formik?.values?.organization}
                              defaultValue={formattedRoles}
                              selectedValuesMaxHeight="64px"
                              options={(roleList || []).map((r) => ({
                                value: r?.id ?? r?.roleId,
                                label: r?.name ?? r?.roleName ?? "-",
                              }))}
                              onChange={(e) => {
                                if (Array.isArray(e)) {
                                  formik.setFieldValue(
                                    "roleIds",
                                    e.map((opt) => opt.value)
                                  );
                                } else if (e?.value != null) {
                                  formik.setFieldValue("roleIds", [e.value]);
                                } else {
                                  formik.setFieldValue("roleIds", []);
                                }
                              }}
                              placeholder={translations[langMode].plxselectanoption}
                              isMulti
                              isClearable
                              height="46px"
                            />
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].department}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].departmentInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <ReactSelector
                            {...selectorMenuProps}
                            dataTestId="department-select"
                            closeMenuOnSelectValue="all"
                            key={formik?.values?.organization}
                            value={
                              formik?.values?.userType === "admin" || formik?.values?.userType === "office_user" || formik?.values?.userType === "supervisor"
                                ? asMultiValue(formattedDepartment)
                                : formattedDepartment
                            }
                            selectedValuesMaxHeight="64px"
                            options={[
                              ...((formik?.values?.userType === "admin" || formik?.values?.userType === "office_user" || formik?.values?.userType === "supervisor") && (department || []).length > 0
                                ? [{ value: "all", label: translations[langMode].all ?? "All" }]
                                : []),
                              ...(department || [])?.map((item) => ({
                                value: String(item.id),
                                label: item.name,
                              })),
                            ]}
                            isOptionSelected={getOptionSelectedWhenAll(
                              allDepartmentsSelected &&
                                (formik?.values?.userType === "admin" || formik?.values?.userType === "office_user" || formik?.values?.userType === "supervisor")
                            )}
                            onChange={(e, meta) => {
                              // Reset project / activity / clock-in / group which depend on the selected department(s)
                              formik.setFieldValue("projectList", []);
                              formik.setFieldValue("workflowList", []);
                              formik.setFieldValue("projectIdForClockInClockOut", null);
                              formik.setFieldValue("groupId", null);
                              setIsAllProject(false);
                              setIsAllActivity(false);
                              setProject([]);
                              setWorkflowList([]);
                              setClockIn([]);
                              setGroupList([]);
                              setGroupCreateMode(false);
                              setGroupCreateValue("");
                              setGroupMenuOpen(false);
                              if (e?.value === 'all') {
                                formik.setFieldValue('departmentList', []);
                              } else if (Array.isArray(e)) {
                                const pickedIds = e.map((option) => option.value);
                                // Picking a specific item while "All" is on drops "All"; ticking "All" wins otherwise.
                                const remainingIds = allDepartmentsSelected
                                  ? getIdsAfterUntick(meta, (department || [])?.map((i) => String(i?.id)))
                                  : null;
                                const isAll = !remainingIds && pickedIds.includes("all") && !(allDepartmentsSelected && pickedIds.some((id) => id !== "all"));
                                const selectedIds = remainingIds ?? pickedIds.filter((id) => id !== "all");
                                formik.setFieldValue(
                                  "departmentList",
                                  isAll ? (department || [])?.map((i) => String(i?.id)) : selectedIds
                                );
                              } else {
                                formik.setFieldValue('departmentList', [String(e?.value)]);
                              }
                            }}
                            placeholder={translations[langMode].plxselectanoption}

                            height="46px"
                            isMulti={formik?.values?.userType === "admin" || formik?.values?.userType === "office_user" || formik?.values?.userType === "supervisor" ? true : false}
                            isClearable={false}
                            onBlur={() => formik.setFieldTouched("departmentList", true)}
                          />
                          {formik.touched.departmentList && formik.errors.departmentList && (
                            <p className="text-xs text-red-500">{formik.errors.departmentList}</p>
                          )}
                        </div>
                        {formik?.values?.userType === "field_agent" && canUseGroups && (
                          <div className="flex flex-col gap-2">
                            <label className="text-sm flex items-center gap-2">
                              <span>{translations[langMode].group ?? "Group"}</span>
                              <FieldInfoIcon
                                tooltipText={
                                  translations[langMode].groupInfo ??
                                  "Assign the field agent to a group in the selected department. Type a new name and click create to add one."
                                }
                              />
                            </label>
                            <ReactSelector
                              {...selectorMenuProps}
                              dataTestId="group-select"
                              isClearable
                              height="46px"
                              isDisabled={!selectedGroupDepartmentId}
                              isLoading={isCreatingGroup}
                              options={groupOptions}
                              value={selectedGroupOption}
                              menuIsOpen={groupMenuOpen}
                              onMenuOpen={() => setGroupMenuOpen(true)}
                              onMenuClose={() => {
                                if (!groupCreateMode) setGroupMenuOpen(false);
                              }}
                              onChange={(option) => {
                                formik.setFieldValue("groupId", option ? option.value : null);
                                setGroupMenuOpen(false);
                              }}
                              menuListHeader={
                                canCreateGroup ? (
                                  <GroupCreateRow
                                    createText={translations[langMode].userFormCreateGroup}
                                    newGroupPlaceholder={translations[langMode].userFormNewGroupName}
                                    cancelLabel={translations[langMode].cancel}
                                    groupCreateMode={groupCreateMode}
                                    groupCreateValue={groupCreateValue}
                                    onStartGroupCreate={startGroupCreate}
                                    onChangeGroupCreate={setGroupCreateValue}
                                    onSubmitGroupCreate={submitGroupCreate}
                                    onCancelGroupCreate={cancelGroupCreate}
                                  />
                                ) : null
                              }
                              noOptionsMessage={() =>
                                translations[langMode].nogroupfound ?? "No group found"
                              }
                              placeholder={translations[langMode].plxselectanoption}
                            />
                          </div>
                        )}
                        {(formik?.values?.userType === "admin" || formik?.values?.userType === "office_user") && canUseGroups && (
                          <div className="flex flex-col gap-2">
                            <label className="text-sm flex items-center gap-2">
                              <span>{translations[langMode].group ?? "Group"}</span>
                              <FieldInfoIcon
                                tooltipText={
                                  translations[langMode].groupListInfo ??
                                  "Select the groups this user can access within the selected department(s)."
                                }
                              />
                            </label>
                            <ReactSelector
                              {...selectorMenuProps}
                              dataTestId="group-list-select"
                              value={selectedGroupListOption}
                              isSearchable
                              isClearable
                              isDisabled={!(formik?.values?.departmentList || []).length}
                              options={(groupList || []).map((g) => ({
                                value: getGroupId(g),
                                label: getGroupName(g),
                              }))}
                              onChange={(option) => {
                                formik.setFieldValue(
                                  "groupList",
                                  option ? [String(option.value)] : []
                                );
                              }}
                              isMulti={false}
                              height="46px"
                              noOptionsMessage={() =>
                                translations[langMode].nogroupfound ?? "No group found"
                              }
                              placeholder={translations[langMode].plxselectanoption}
                              onBlur={() => formik.setFieldTouched("groupList", true)}
                            />
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].project}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].projectInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <ReactSelector
                            {...selectorMenuProps}
                            dataTestId="project-select"
                            closeMenuOnSelectValue="all"
                            key={formik?.values?.departmentList}
                            value={asMultiValue(formattedProject)}
                            selectedValuesMaxHeight="64px"
                            options={[
                              ...((Object.values(project)?.flat() || []).length > 0
                                ? [{ value: "all", label: translations[langMode].all ?? "All" }]
                                : []),
                              ...(Object.values(project)?.flat() || []).map((item) => ({
                                value: String(item.id),
                                label: item.name,
                              })),
                            ]}
                            isOptionSelected={getOptionSelectedWhenAll(selectedAllProject)}
                            onChange={(e, meta) => {
                              // Reset activity / clock-in which depend on the selected project(s)
                              formik.setFieldValue("workflowList", []);
                              formik.setFieldValue("projectIdForClockInClockOut", null);
                              setIsAllActivity(false);
                              setWorkflowList([]);
                              setClockIn([]);
                              if (e?.value === 'all') {
                                setIsAllProject(true);
                                formik.setFieldValue("projectList", []); // Set to empty array when "All" is selected
                              } else if (Array.isArray(e)) {
                                const pickedIds = e.map((option) => option.value);
                                // Picking a specific item while "All" is on drops "All"; ticking "All" wins otherwise.
                                const remainingIds = selectedAllProject
                                  ? getIdsAfterUntick(meta, (Object.values(project)?.flat() || []).map((i) => String(i?.id)))
                                  : null;
                                const isAll = !remainingIds && pickedIds.includes("all") && !(selectedAllProject && pickedIds.some((id) => id !== "all"));
                                const selectedIds = remainingIds ?? pickedIds.filter((id) => id !== "all");
                                setIsAllProject(isAll);
                                formik.setFieldValue(
                                  "projectList",
                                  isAll
                                    ? (Object.values(project)?.flat() || [])?.map((i) => String(i?.id))
                                    : selectedIds
                                );
                              } else {
                                setIsAllProject(false);
                                formik.setFieldValue('projectList', [String(e?.value)]);
                              }
                            }}
                            isClearable={false}
                            isMulti={true}
                            height="46px"
                            placeholder={translations[langMode].plxselectanoption}
                            onBlur={() => formik.setFieldTouched("projectList", true)}
                          />
                          {formik.touched.projectList && formik.errors.projectList && (
                            <p className="text-xs text-red-500">{formik.errors.projectList}</p>
                          )}
                        </div>
                        <div className={`${!isClock ? "hidden" : ""} flex flex-col gap-2`}>
                          <label className="text-sm flex items-center gap-2">
                            <span>Clock In</span>
                            <FieldInfoIcon tooltipText={translations[langMode].clockInInfo} />
                            {hasClockInOptions && <span className="text-[#FF9F29]">*</span>}
                          </label>
                          <ReactSelector
                            {...selectorMenuProps}
                            dataTestId="clockin-select"
                            height="46px"
                            value={formattedClockIn || null}
                            options={clockInOptions}
                            onChange={(e) => {
                              formik.setFieldValue("projectIdForClockInClockOut", e?.value);
                            }}
                            onBlur={() => formik.setFieldTouched("projectIdForClockInClockOut", true)}
                          />
                          {formik.touched.projectIdForClockInClockOut && formik.errors.projectIdForClockInClockOut && (
                            <p className="text-xs text-red-500">{formik.errors.projectIdForClockInClockOut}</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].Activity}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].activityInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <ReactSelector
                            {...selectorMenuProps}
                            dataTestId="activity-select"
                            closeMenuOnSelectValue="all"
                            key={formik?.values?.projectList}
                            value={asMultiValue(formattedActivity)}
                            selectedValuesMaxHeight="64px"
                            options={[
                              ...((formik?.values?.userType !== "admin" || formik?.values?.userType !== "office_user") && (workflowList || []).length > 0
                                ? [{ value: "all", label: translations[langMode].all ?? "All" }]
                                : []),
                              ...(workflowList || [])?.map((item) => ({
                                "value": item?.workflowId,
                                "label": item?.workflowName,
                              }))
                            ]}
                            isOptionSelected={getOptionSelectedWhenAll(selectedAllActivity)}
                            onChange={(e, meta) => {
                              if (e?.value === 'all') {
                                setIsAllActivity(true);
                                formik.setFieldValue("workflowList", (workflowList || [])?.map((item) => item?.workflowId));
                              } else if (Array.isArray(e)) {
                                const pickedIds = e.map((option) => option.value);
                                // Picking a specific item while "All" is on drops "All"; ticking "All" wins otherwise.
                                const remainingIds = selectedAllActivity
                                  ? getIdsAfterUntick(meta, (workflowList || [])?.map((i) => i?.workflowId))
                                  : null;
                                const isAll = !remainingIds && pickedIds.includes("all") && !(selectedAllActivity && pickedIds.some((id) => id !== "all"));
                                const selectedIds = remainingIds ?? pickedIds.filter((id) => id !== "all");
                                setIsAllActivity(isAll);
                                formik.setFieldValue("workflowList", isAll ? (workflowList || [])?.map(i => i?.workflowId) : selectedIds);
                              } else {
                                setIsAllActivity(false);
                                formik.setFieldValue("workflowList", e?.map((item) => item?.value));
                              }
                            }}
                            // isDisabled={formik?.values?.userType === "admin" || formik?.values?.userType === "office_user"}
                            isMulti={true}
                            height="46px"
                            onBlur={() => formik.setFieldTouched("workflowList", true)}
                          />
                          {formik.touched.workflowList && formik.errors.workflowList && (
                            <p className="text-xs text-red-500">{formik.errors.workflowList}</p>
                          )}
                        </div>
                        {/* employed id added by harshit vachhani */}
                        <div className={`${!showFieldAgentIdentityFields ? "hidden" : ""} flex flex-col gap-2`}>
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].employeeIdLabel}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].employeeIdInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <input
                            type="number"
                            name="employeeId"
                            data-testid="employeeId"
                            label={translations[langMode].employee}
                            value={formik.values.employeeId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`block p-3 w-full text-sm text-gray-900 bg-transparent rounded-[10px] border-1 border-[#D1D5DB]
                    appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 peer ${theme === "dark"
                                ? "dark-mode"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : ""
                              }`}
                          />
                          {formik.touched.employeeId && formik.errors.employeeId && (
                            <p className="text-xs text-red-500">{formik.errors.employeeId}</p>
                          )}
                        </div>
                        {/*  subContract added by harshit vachhani */}
                        <div className={`${!showFieldAgentIdentityFields ? "hidden" : ""} flex flex-col gap-2`}>
                          <label className="text-sm flex items-center gap-2">
                            <span>{translations[langMode].subContractIdLabel}</span>
                            <FieldInfoIcon tooltipText={translations[langMode].subContractInfo} />
                            <span className="text-[#FF9F29]">*</span>
                          </label>
                          <input
                            type="text"
                            name="subContract"
                            data-testid="subContract"
                            label={translations[langMode].subContract}
                            value={formik.values.subContract}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={`block p-3 w-full text-sm text-gray-900 bg-transparent rounded-[10px] border-1 border-[#D1D5DB]
                    appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 peer ${theme === "dark"
                                ? "dark-mode"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : ""
                              }`}
                          />
                          {formik.touched.subContract && formik.errors.subContract && (
                            <p className="text-xs text-red-500">{formik.errors.subContract}</p>
                          )}
                        </div>
                        {formik?.values?.userType !== "field_agent" && (
                          <div className=" relative flex flex-col gap-2">
                            <label className="text-sm flex items-center gap-2">
                              <span>{translations[langMode].password}</span>
                              <FieldInfoIcon tooltipText={translations[langMode].passwordInfo} />
                              {!userId && <span className="text-[#FF9F29]">*</span>}
                            </label>
                            <div className="relative w-full">
                              <input
                                data-testid="password-input"
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formik.values.password}
                                placeholder={userId ? translations[langMode].userFormLeaveBlank : ""}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  formik.setFieldValue("password", newValue);
                                  if (userId && newValue) {
                                    setIsPasswordCleared(true);
                                  }
                                }}
                                onBlur={formik.handleBlur}
                                className={`block p-3 ${langMode === "ar" ? "pl-10" : "pr-10"} w-full text-sm text-gray-900 bg-transparent rounded-[10px] border-1 border-[#D1D5DB]
                  appearance-none focus:outline-none focus:ring-0 focus:border-gray-500 peer ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
                                  }`}
                              />
                              <button
                                type="button"
                                data-testid="password-toggle-button"
                                onClick={() => setShowPassword(!showPassword)}
                                className={`absolute top-1/2 -translate-y-1/2 ${langMode === "ar" ? "left-3" : "right-3"} text-sm text-gray-400`}
                              >
                                {showPassword ? <FaEye /> : <FaEyeSlash />}
                              </button>
                            </div>

                            {formik.touched.password && !passwordValue && formik.errors.password && (
                              <p className="text-xs text-red-500">{formik.errors.password}</p>
                            )}

                            {showPasswordChecklist && (
                              <ul className={passwordChecklistClass}>
                                {passwordChecklist.map((rule) => (
                                  <li
                                    key={rule.label}
                                    className={`flex items-center gap-2 text-[11px] ${rule.valid ? "text-green-600" : "text-gray-400"
                                      }`}
                                  >
                                    <span>{rule.valid ? "✔" : "○"}</span>
                                    <span>{rule.label}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                        {formik?.values?.userType === "field_agent" && (
                          <div
                            className={`xl:col-span-2 lg:col-span-2 md:col-span-2 sm:col-span-2 col-span-1 mt-2 w-full max-w-[320px] rounded-xl border border-[#D9E6F5] bg-[#F8FBFF] p-2 shadow-sm ${theme === "dark"
                              ? "dark-mode"
                              : theme === "high-contrast"
                                ? "high-contrast"
                                : ""
                              }`}
                          >
                            <div className="mb-3">
                              <p className="text-base font-semibold text-[#111827] flex items-center gap-2">
                                <span>{translations[langMode].Signature ?? "Signature"}</span>
                                <FieldInfoIcon tooltipText={translations[langMode].signatureInfo} />
                              </p>
                              <p className="mt-1 text-xs text-[#6B7280]">
                                {translations[langMode].signatureInfo}
                              </p>
                            </div>
                            <div
                              className={`mb-3 flex h-[140px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-[#236DB4] bg-white p-3 ${theme === "dark"
                                ? "dark-mode"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : ""
                                }`}
                            >
                              {signaturePreview ? (
                                <>
                                  <img
                                    src={signaturePreview}
                                    alt="signature preview"
                                    className="mb-2 h-[72px] w-full rounded-md object-contain"
                                  />
                                  <div className="rounded-full bg-[#E8F1FB] px-3 py-1 text-xs font-medium text-[#236DB4]">
                                    {signatureFileLabel}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#E8F1FB] text-[#236DB4]">
                                    <RiUploadCloud2Line className="text-[24px]" />
                                  </div>
                                  <p className="text-sm font-medium text-[#111827]">{translations[langMode].userFormUploadSignature}</p>
                                  <p className="mt-1 text-center text-xs text-[#6B7280]">
                                    {translations[langMode].userFormSignatureFormats}
                                  </p>
                                  <p className="mt-1 text-center text-xs text-[#9CA3AF]">
                                    {translations[langMode].maxFileSize5MB || "Max file size: 5MB"}
                                  </p>
                                </>
                              )}
                            </div>

                            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#236DB4] px-4 py-2.5 text-sm font-medium text-white transition hover:bg-[#1d5a95]">
                              <RiUploadCloud2Line className="text-lg" />
                              {signaturePreview ? translations[langMode].userFormReplaceSignature : translations[langMode].userFormChooseSignature}
                              <input
                                type="file"
                                name="signature"
                                onChange={handleSignatureUpload}
                                accept="image/jpeg, image/png, image/gif, image/jpg"
                                className="hidden"
                              />
                            </label>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className={actionFooterClass}>
                  <div className="mb-0">
                    <div className={actionButtonRowClass}>
                      <CommonButton colorIntent="danger"
                        variant='danger'
                        type="button"
                        onClick={handleGoBack}
                        data-testid="cancel-button"
                      >
                        {translations[langMode].cancel}
                      </CommonButton>
                      <CommonButton colorIntent="success"
                        type="submit"
                        variant='primary'
                        data-testid="submit-button"
                      >
                        {userId ? (isLoader ? translations[langMode].update : translations[langMode].update) : (isLoader ? translations[langMode].Saving : translations[langMode].save)}
                      </CommonButton>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            </form>
          </div>

        </div>
      </div >
    </>
  );
};

export default AddUser;
